import React, { useMemo, useState } from "react";
import { GetSingleTrackResponse, SingleTrackResponse } from "../../types/requestsOutputs";
import { FaVolumeUp } from "react-icons/fa";
import { ReactComponent as EditIcon } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-bin-line.svg";
import { ReactComponent as EyeIcon } from "../../assets/images/eye-line.svg";
import { ReactComponent as QRCodeIcon } from "../../assets/images/qr-code-line.svg";
import ToggleSwitch from "../common/ToggleSwitch";
import { deleteSingleTrack, updateSingleTrack } from "../../api/singleTrack";
import { useTranslation } from "react-i18next";
import DeleteModal from "../modals/DeleteModal";
import UnifiedEditSingleTrackModal from "../modals/UnifiedEditSingleTrackModal";
import { Tooltip } from "@mui/material";
import { useGlobalState } from "../../context/useGlobalState";
import { CompanySingleTrackFolder } from "../../types/requestsOutputs";
import styles from "./SingleTrack.module.css";

interface Props {
    singleTracks: GetSingleTrackResponse;
    locale?: string;
    refetch?: () => Promise<void>;
    onQRCodeDownload?: (otpTokenId: string) => Promise<void>;
    hub?: CompanySingleTrackFolder | null;
    folderId?: string | null;
    categoryId?: string | null;
}

// Group single tracks by their main ID
interface GroupedSingleTrack {
    mainTrack: SingleTrackResponse["singleTrack"];
    localizedVersions: SingleTrackResponse[];
}

const getSortableName = (track: GroupedSingleTrack, hubLang: string = "FR") => {
    // 1. Try CMS language
    const cmsVersion = track.localizedVersions.find((v) => v.language.toUpperCase() === hubLang);
    if (cmsVersion?.name) return cmsVersion.name;

    // 2. Try fallback priority (French → English)
    const priority = ["FR", "EN"];
    for (const lang of priority) {
        const version = track.localizedVersions.find((v) => v.language.toUpperCase() === lang);
        if (version?.name) return version.name;
    }

    // 3. Finally, take the first available
    return track.localizedVersions[0]?.name || "";
};

export const SingleTrackPanel: React.FC<Props> = ({
    singleTracks,
    locale = "en",
    refetch,
    onQRCodeDownload,
    hub,
    folderId,
    categoryId,
}) => {
    const [optimisticallyDeletedIds, setOptimisticallyDeletedIds] = useState<Set<string>>(new Set());

    const groupedTracks = useMemo(() => {
        const grouped: { [key: string]: GroupedSingleTrack } = {};

        singleTracks?.forEach((track) => {
            const mainId = track.singleTrack.id;
            if (!grouped[mainId]) {
                grouped[mainId] = {
                    mainTrack: track.singleTrack,
                    localizedVersions: [],
                };
            }
            grouped[mainId].localizedVersions.push(track);
        });
        // Filter out optimistically deleted tracks
        const filteredGrouped = Object.values(grouped).filter(
            (groupedTrack) => !optimisticallyDeletedIds.has(groupedTrack.mainTrack.id),
        );

        return filteredGrouped.sort((a, b) => {
            const posA = a.mainTrack.position || 0;
            const posB = b.mainTrack.position || 0;

            if (posA > 0 && posB > 0 && posA !== posB) return posA - posB;
            if (posA > 0 && posB === 0) return -1;
            if (posB > 0 && posA === 0) return 1;

            // both 0 → sort by name consistently
            return getSortableName(a, hub?.originLanguageId?.toUpperCase() || "FR").localeCompare(
                getSortableName(b, hub?.originLanguageId?.toUpperCase() || "FR"),
            );
        });
        // Sort uses hub origin language; hub is stable for this panel
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleTracks, optimisticallyDeletedIds]);

    const handleOptimisticDelete = async (trackId: string) => {
        // Optimistic update: immediately remove from UI
        setOptimisticallyDeletedIds((prev) => new Set(prev).add(trackId));

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("No token found");
            }

            await deleteSingleTrack({ token, trackId });
            // Success - item stays hidden
        } catch (error) {
            console.error("Failed to delete singleTrack", error);
            // Revert the optimistic update on error
            setOptimisticallyDeletedIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(trackId);
                return newSet;
            });
        }
    };

    return (
        <div className={styles.panel}>
            {groupedTracks.map((groupedTrack) => (
                <SingleTrackCard
                    key={groupedTrack.mainTrack.id}
                    groupedTrack={groupedTrack}
                    locale={locale}
                    refetch={refetch}
                    onQRCodeDownload={onQRCodeDownload}
                    onDelete={handleOptimisticDelete}
                    hub={hub}
                    folderId={folderId}
                    categoryId={categoryId}
                />
            ))}
        </div>
    );
};

interface CardProps {
    groupedTrack: GroupedSingleTrack;
    locale: string;
    refetch?: () => Promise<void>;
    onQRCodeDownload?: (otpTokenId: string) => Promise<void>;
    onDelete?: (trackId: string) => Promise<void>;
    hub?: CompanySingleTrackFolder | null;
    folderId?: string | null;
    categoryId?: string | null;
}

const SingleTrackCard: React.FC<CardProps> = ({
    groupedTrack,
    locale,
    refetch,
    onQRCodeDownload,
    onDelete,
    hub,
    folderId,
    categoryId,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [activeTab] = useState(0);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isQRCodeLoading, setIsQRCodeLoading] = useState(false);
    const [optimisticStatus, setOptimisticStatus] = useState<boolean | null>(null);

    const { mainTrack, localizedVersions } = groupedTrack;

    const sortLanguages = (versions: SingleTrackResponse[], hubLang: string = "FR") => {
        const priority = ["FR", "EN"]; // French first, then English
        return versions.sort((a, b) => {
            const langA = a.language.toUpperCase();
            const langB = b.language.toUpperCase();

            // CMS lang always first
            if (langA === hubLang) return -1;
            if (langB === hubLang) return 1;

            // Then by priority array
            const idxA = priority.indexOf(langA);
            const idxB = priority.indexOf(langB);

            if (idxA !== -1 && idxB === -1) return -1;
            if (idxB !== -1 && idxA === -1) return 1;
            if (idxA !== -1 && idxB !== -1) return idxA - idxB;

            // Finally alphabetical
            return langA.localeCompare(langB);
        });
    };

    const orderedLocalizedVersions = useMemo(
        () => sortLanguages(localizedVersions, hub?.originLanguageId || "FR"),
        [localizedVersions, hub?.originLanguageId],
    );

    // Use optimistic status if available, otherwise use the actual status
    const currentStatus = optimisticStatus !== null ? optimisticStatus : mainTrack.status;

    // Get available languages
    const { data } = useGlobalState();
    const availableLanguages = orderedLocalizedVersions.map((version) => {
        const match = data.languagesAI?.find((lang) => lang.code.toUpperCase() === version.language.toUpperCase());

        return {
            code: version.language,
            name: match?.label || version.language, // fallback to raw code
        };
    });

    const currentLocalizedVersion = orderedLocalizedVersions[activeTab];

    const handleDelete = async () => {
        if (onDelete) {
            await onDelete(mainTrack.id);
        }
    };

    const handleQRCodeDownload = async () => {
        if (!mainTrack.otpToken?.[0].token || !onQRCodeDownload) return;

        setIsQRCodeLoading(true);
        try {
            await onQRCodeDownload(mainTrack.otpToken?.[0].token);
        } catch (error) {
            console.error("Failed to download QR code:", error);
        } finally {
            setIsQRCodeLoading(false);
        }
    };

    const handleToggleStatus = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;

            // Optimistic update: immediately update the UI based on current status
            const newStatus = !currentStatus;
            setOptimisticStatus(newStatus);

            await updateSingleTrack({
                token,
                trackId: mainTrack.id,
                data: { status: newStatus },
            });
        } catch (error) {
            console.error("Failed to update track status:", error);
            // Revert the optimistic update on error
            setOptimisticStatus(null);
        }
    };

    return (
        <div className={styles.rowCard}>
            {mainTrack.coverImage ? (
                <div className={styles.thumb}>
                    <img src={mainTrack.coverImage} alt="Cover" />
                </div>
            ) : (
                <div className={styles.rowTypeIcon}>
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M9 18V5l12-2v13" />
                        <circle cx="6" cy="18" r="3" />
                        <circle cx="18" cy="16" r="3" />
                    </svg>
                </div>
            )}

            <div className={styles.rowInfo}>
                <p className={styles.rowName}>{currentLocalizedVersion?.name || t("noNameProvided")}</p>
                <p className={styles.rowMeta}>
                    {mainTrack.category?.internalName || ""}
                    {mainTrack.position ? ` · ${t("position")} ${mainTrack.position}` : ""}
                    {availableLanguages.length > 0 && (
                        <span className={styles.langBadges}>
                            {availableLanguages.map((lang, idx) => (
                                <span key={lang.code} className={styles.langBadge}>
                                    {lang.code.toUpperCase()}
                                    {orderedLocalizedVersions[idx]?.audioTrackId && (
                                        <FaVolumeUp
                                            style={{ fontSize: "8px", marginLeft: "2px", color: "var(--green-600)" }}
                                        />
                                    )}
                                </span>
                            ))}
                        </span>
                    )}
                </p>
            </div>

            <div className={styles.statusToggle} onClick={(e) => e.stopPropagation()}>
                <span className={`${styles.statusLabel}${currentStatus ? ` ${styles.statusLabelActive}` : ""}`}>
                    {currentStatus ? t("active") : t("inactive")}
                </span>
                <ToggleSwitch checked={currentStatus || false} onChange={handleToggleStatus} />
            </div>

            <div className={styles.rowSeparator} />

            <div className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
                <Tooltip title={t("viewContent")} arrow>
                    <EyeIcon
                        className={styles.mutedIcon}
                        style={{
                            opacity: hub?.oTPToken?.[0]?.token && folderId && categoryId ? 1 : 0.35,
                            cursor: hub?.oTPToken?.[0]?.token && folderId && categoryId ? "pointer" : "not-allowed",
                        }}
                        onClick={() => {
                            const hubToken = hub?.oTPToken?.[0]?.token;
                            if (!hubToken || !folderId || !categoryId) return;
                            window.open(
                                `https://hub.rewindstories.com/${hubToken}/${folderId}/${categoryId}/${mainTrack.id}`,
                                "_blank",
                            );
                        }}
                    />
                </Tooltip>

                {mainTrack.otpToken?.[0]?.id && onQRCodeDownload && (
                    <Tooltip title={t("downloadQRCode")} arrow>
                        <QRCodeIcon
                            className={styles.mutedIcon}
                            style={{ opacity: isQRCodeLoading ? 0.4 : 1 }}
                            onClick={handleQRCodeDownload}
                        />
                    </Tooltip>
                )}

                <Tooltip title={t("edit")} arrow>
                    <EditIcon className={styles.settingsIcon} onClick={() => setIsEditModalOpen(true)} />
                </Tooltip>

                <Tooltip title={t("delete")} arrow>
                    <DeleteIcon className={styles.deleteIcon} onClick={() => setIsDeleteModalOpen(true)} />
                </Tooltip>
            </div>

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                message={t("deleteSingleTrackConfirmation")}
                title={t("deleteSingleTrack")}
            />
            <UnifiedEditSingleTrackModal
                singleTrack={{ mainTrack, localizedVersions }}
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSuccess={() => {
                    if (refetch) {
                        refetch();
                    }
                }}
                hub={hub}
            />
        </div>
    );
};
